import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { getApiBaseUrl } from '../../services/httpClient';
import {
  CITIZEN_APP_NAME,
  CITIZEN_PLAY_STORE,
  DEMO_COMPARE,
  DEMO_COPY,
  DEMO_FAQS,
  DEMO_FEATURES,
  DEMO_NAV,
  DEMO_OUTLETS,
  DEMO_PURPOSE_OPTIONS,
  VYAPAR_APP_NAME,
  VYAPAR_PLAY_STORE,
  t,
} from './loksy-landing/demoContent';
import './loksy-landing/loksyLanding.css';
import './loksy-landing/loksyDemo.css';

const API_BASE_URL = getApiBaseUrl();
const LANGS = [
  { id: 'en', label: 'EN' },
  { id: 'te', label: 'TE' },
  { id: 'hi', label: 'HI' },
];

const emptyForm = {
  person_name: '',
  email: '',
  phone: '',
  business_name: '',
  city: '',
  category: '',
  purpose: 'Loksy Vyapar full demo',
  custom_purpose: '',
  preferred_demo_time: '',
  app_target: 'both',
  message: '',
  consent: true,
};

export default function LoksyDemo() {
  const [lang, setLang] = useState('en');
  const [zoneFilter, setZoneFilter] = useState('all');
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const [callNumbers, setCallNumbers] = useState([]);

  useEffect(() => {
    document.title = 'Loksy Vyapar Demo — Billing, Tokens, Ads & Local Discovery';
    document.body.classList.add('loksy-landing-active');
    return () => document.body.classList.remove('loksy-landing-active');
  }, []);

  const features = useMemo(
    () =>
      zoneFilter === 'all'
        ? DEMO_FEATURES
        : DEMO_FEATURES.filter((f) => f.zone === zoneFilter),
    [zoneFilter],
  );

  async function onSubmit(e) {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API_BASE_URL}/enquiries/public/demo`, {
        person_name: form.person_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        business_name: form.business_name.trim(),
        city: form.city.trim(),
        category: form.category.trim(),
        work_purpose: form.purpose,
        custom_purpose: form.custom_purpose.trim(),
        preferred_demo_time: form.preferred_demo_time.trim(),
        app_target: form.app_target,
        remarks: form.message.trim(),
        language: lang,
        callback_consent: form.consent,
        page_source: 'loksy.in/demo',
      });
      if (!data?.success) throw new Error(data?.message || 'Failed to book demo');
      setCallNumbers(Array.isArray(data.call_numbers) ? data.call_numbers : []);
      setDone(true);
      setForm(emptyForm);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          'Could not book demo. Please try again or call us.',
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className={`loksy-landing loksy-demo ${lang === 'te' ? 'is-te' : ''} ${lang === 'hi' ? 'is-hi' : ''}`}>
      <header className="loksy-demo-header">
        <div className="loksy-demo-header-inner">
          <Link to="/" className="loksy-demo-brand">
            Loksy<span />
          </Link>
          <nav className="loksy-demo-nav">
            {DEMO_NAV.map((item) => (
              <a key={item.href} href={item.href}>
                {t(item.label, lang)}
              </a>
            ))}
          </nav>
          <div className="loksy-demo-header-actions">
            <div className="loksy-demo-langs">
              {LANGS.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className={lang === item.id ? 'active' : ''}
                  onClick={() => setLang(item.id)}
                >
                  {item.label}
                </button>
              ))}
            </div>
            <a href="#book-demo" className="loksy-demo-btn primary hide-sm">
              {t(DEMO_COPY.ctaDemo, lang)}
            </a>
          </div>
        </div>
      </header>

      <main>
        <section className="loksy-demo-hero">
          <div className="loksy-demo-hero-grid">
            <div>
              <p className="eyebrow">{t(DEMO_COPY.brandEyebrow, lang)}</p>
              <h1>{t(DEMO_COPY.heroHeadline, lang)}</h1>
              <p className="sub">{t(DEMO_COPY.heroTitle, lang)}</p>
              <p className="body">{t(DEMO_COPY.heroBody, lang)}</p>
              <div className="cta-row">
                <a href="#book-demo" className="loksy-demo-btn primary">
                  {t(DEMO_COPY.ctaDemo, lang)}
                </a>
                <a
                  href={CITIZEN_PLAY_STORE}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="loksy-demo-btn ghost"
                >
                  {t(DEMO_COPY.ctaCitizen, lang)}
                </a>
                <a
                  href={VYAPAR_PLAY_STORE}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="loksy-demo-btn ghost"
                >
                  {t(DEMO_COPY.ctaVyapar, lang)}
                </a>
              </div>
              <p className="trust">{t(DEMO_COPY.trustLine, lang)}</p>
            </div>
            <div className="hero-phone-wrap">
              <div className="hero-phone">
                <img
                  src="/images/demo/features/mitra-dashboard.png"
                  alt="Loksy Vyapar dashboard"
                />
              </div>
              <div className="hero-app-badges">
                <img src="/images/demo/apps/vyapar-icon.png" alt="Loksy Vyapar app icon" />
                <img src="/images/demo/apps/citizen-icon.png" alt="Loksy Citizen app icon" />
              </div>
            </div>
          </div>
        </section>

        <section id="ecosystem" className="loksy-demo-section night">
          <h2>{t(DEMO_COPY.ecosystemTitle, lang)}</h2>
          <p className="lead">{t(DEMO_COPY.ecosystemBody, lang)}</p>
          <div className="eco-grid">
            {[
              {
                title: VYAPAR_APP_NAME,
                body: 'My Business Desk',
                href: '/loksymitra',
                icon: '/images/demo/apps/vyapar-icon.png',
                thumb: '/images/demo/features/mitra-dashboard.png',
              },
              {
                title: 'Connect with Loksy',
                body: 'Grow on Citizen',
                href: '#connect',
                icon: '/images/demo/apps/vyapar-icon.png',
                thumb: '/images/demo/features/business_details_card.webp',
              },
              {
                title: CITIZEN_APP_NAME,
                body: 'Discover · Order · Return',
                href: '/loksyapp',
                icon: '/images/demo/apps/citizen-icon.png',
                thumb: '/images/demo/features/citizen-home.png',
              },
            ].map((card) => (
              <a key={card.title} href={card.href} className="eco-card eco-card-pro">
                <div className="eco-card-top">
                  <img className="eco-app-icon" src={card.icon} alt="" />
                  <div>
                    <strong>{card.title}</strong>
                    <span>{card.body}</span>
                  </div>
                </div>
                <div className="eco-thumb-frame">
                  <img src={card.thumb} alt="" />
                </div>
              </a>
            ))}
          </div>
        </section>

        <section id="operate" className="loksy-demo-section">
          <p className="eyebrow">01 · Operate</p>
          <h2>{t(DEMO_COPY.operateTitle, lang)}</h2>
          <p className="lead">{t(DEMO_COPY.operateBody, lang)}</p>
        </section>
        <section id="connect" className="loksy-demo-section dim">
          <p className="eyebrow">02 · Connect</p>
          <h2>{t(DEMO_COPY.connectTitle, lang)}</h2>
          <p className="lead">{t(DEMO_COPY.connectBody, lang)}</p>
        </section>
        <section id="citizen" className="loksy-demo-section">
          <p className="eyebrow">03 · Citizen</p>
          <h2>{t(DEMO_COPY.citizenTitle, lang)}</h2>
          <p className="lead">{t(DEMO_COPY.citizenBody, lang)}</p>
        </section>

        <section className="loksy-demo-section night">
          <div className="filter-row">
            {[
              ['all', 'All'],
              ['operate', 'Business Desk'],
              ['connect', 'Connect'],
              ['citizen', 'Citizen'],
            ].map(([id, label]) => (
              <button
                key={id}
                type="button"
                className={zoneFilter === id ? 'chip active' : 'chip'}
                onClick={() => setZoneFilter(id)}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="feature-grid">
            {features.map((feature) => (
              <article key={feature.id} className="feature-card">
                <img src={feature.image} alt="" />
                <p className="zone">{feature.zone}</p>
                <h3>{t(feature.title, lang)}</h3>
                <p>
                  <span className="tag problem">{t(DEMO_COPY.problemLabel, lang)}</span>
                  {t(feature.problem, lang)}
                </p>
                <p>
                  <span className="tag solution">{t(DEMO_COPY.solutionLabel, lang)}</span>
                  {t(feature.solution, lang)}
                </p>
                <p>
                  <span className="tag advantage">{t(DEMO_COPY.advantageLabel, lang)}</span>
                  {t(feature.advantage, lang)}
                </p>
              </article>
            ))}
          </div>
        </section>

        <section id="outlets" className="loksy-demo-section">
          <h2>{t(DEMO_COPY.outletsTitle, lang)}</h2>
          <div className="outlet-grid">
            {DEMO_OUTLETS.map((outlet) => (
              <div key={outlet.id}>{t(outlet.label, lang)}</div>
            ))}
          </div>
        </section>

        <section className="loksy-demo-section night">
          <h2>{t(DEMO_COPY.compareTitle, lang)}</h2>
          <div className="compare-table">
            <div className="compare-head">
              <span>Notebook</span>
              <span>Loksy</span>
            </div>
            {DEMO_COMPARE.map((row, i) => (
              <div key={i} className="compare-row">
                <span>{t(row.notebook, lang)}</span>
                <span>{t(row.loksy, lang)}</span>
              </div>
            ))}
          </div>
        </section>

        <section id="book-demo" className="loksy-demo-section book">
          <div className="book-grid">
            <div>
              <h2>{t(DEMO_COPY.formTitle, lang)}</h2>
              <p className="lead">{t(DEMO_COPY.formBody, lang)}</p>
              <div className="book-mascot-wrap">
                <img className="book-mascot" src="/images/loki/loki_leads.png" alt="Loki guide" />
                <p className="mascot-caption">Loki helps you book a walkthrough — one guide for the whole Loksy loop.</p>
              </div>
              <p className="trust">{t(DEMO_COPY.downloadTitle, lang)}</p>
              <div className="cta-row">
                <a href={CITIZEN_PLAY_STORE} target="_blank" rel="noopener noreferrer" className="loksy-demo-btn ghost">
                  {CITIZEN_APP_NAME}
                </a>
                <a href={VYAPAR_PLAY_STORE} target="_blank" rel="noopener noreferrer" className="loksy-demo-btn primary">
                  {VYAPAR_APP_NAME}
                </a>
              </div>
            </div>
            <div className="book-form-wrap">
              {done ? (
                <div className="book-success">
                  <h3>Demo request saved</h3>
                  <p>Our team will call you soon. It appears under Loksy Leads → Demo Requests.</p>
                  {callNumbers.length > 0 && (
                    <p>
                      Or call{' '}
                      {callNumbers.map((n, i) => (
                        <span key={n}>
                          {i > 0 ? ' / ' : ''}
                          <a href={`tel:+91${n}`}>{n}</a>
                        </span>
                      ))}
                    </p>
                  )}
                  <button type="button" className="loksy-demo-btn ghost" onClick={() => setDone(false)}>
                    Book another
                  </button>
                </div>
              ) : (
                <form onSubmit={onSubmit}>
                  <label>
                    Name *
                    <input required value={form.person_name} onChange={(e) => setForm((f) => ({ ...f, person_name: e.target.value }))} />
                  </label>
                  <label>
                    Email
                    <input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
                  </label>
                  <label>
                    Phone (10 digits) *
                    <input required inputMode="tel" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} />
                  </label>
                  <label>
                    Shop / business name *
                    <input required value={form.business_name} onChange={(e) => setForm((f) => ({ ...f, business_name: e.target.value }))} />
                  </label>
                  <div className="two-col">
                    <label>
                      City / town *
                      <input required value={form.city} onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))} />
                    </label>
                    <label>
                      Category
                      <input placeholder="Cafe, kirana…" value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} />
                    </label>
                  </div>
                  <label>
                    Demo interest
                    <select value={form.purpose} onChange={(e) => setForm((f) => ({ ...f, purpose: e.target.value }))}>
                      {DEMO_PURPOSE_OPTIONS.map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </label>
                  {form.purpose === 'Other enquiry' && (
                    <label>
                      Describe interest
                      <input value={form.custom_purpose} onChange={(e) => setForm((f) => ({ ...f, custom_purpose: e.target.value }))} />
                    </label>
                  )}
                  <label>
                    App focus
                    <select value={form.app_target} onChange={(e) => setForm((f) => ({ ...f, app_target: e.target.value }))}>
                      <option value="loksy_vyapar">{VYAPAR_APP_NAME}</option>
                      <option value="citizen">{CITIZEN_APP_NAME}</option>
                      <option value="both">Both</option>
                    </select>
                  </label>
                  <label>
                    Preferred demo time
                    <input placeholder="Weekday evenings…" value={form.preferred_demo_time} onChange={(e) => setForm((f) => ({ ...f, preferred_demo_time: e.target.value }))} />
                  </label>
                  <label>
                    Message
                    <textarea value={form.message} onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))} />
                  </label>
                  <label className="consent">
                    <input type="checkbox" checked={form.consent} required onChange={(e) => setForm((f) => ({ ...f, consent: e.target.checked }))} />
                    I agree to a callback from the Loksy team about this demo.
                  </label>
                  {error && <p className="form-error">{error}</p>}
                  <button type="submit" className="loksy-demo-btn primary wide" disabled={submitting}>
                    {submitting ? 'Saving…' : t(DEMO_COPY.ctaDemo, lang)}
                  </button>
                </form>
              )}
            </div>
          </div>
        </section>

        <section className="loksy-demo-section">
          <h2>{t(DEMO_COPY.faqTitle, lang)}</h2>
          <div className="faq-list">
            {DEMO_FAQS.map((faq, i) => (
              <details key={i}>
                <summary>{t(faq.q, lang)}</summary>
                <p>{t(faq.a, lang)}</p>
              </details>
            ))}
          </div>
        </section>
      </main>

      <div className="loksy-demo-mobile-bar">
        <a href="#book-demo">Demo</a>
        <a href={CITIZEN_PLAY_STORE} target="_blank" rel="noopener noreferrer">Citizen</a>
        <a href={VYAPAR_PLAY_STORE} target="_blank" rel="noopener noreferrer">Vyapar</a>
      </div>

      <footer className="loksy-demo-footer">
        <Link to="/">loksy.in</Link>
        {' · '}
        <Link to="/loksymitra">{VYAPAR_APP_NAME} story</Link>
        {' · '}
        <Link to="/loksyapp">{CITIZEN_APP_NAME} story</Link>
      </footer>
    </div>
  );
}
